import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet-async';
import {
  BellIcon, CheckIcon,
} from '@heroicons/react/24/outline';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import { PageHeader } from '../../components/layout';
import { Card, CardBody, Button, EmptyState, PageLoader, Badge, Avatar } from '../../components/ui';
import { useNotificationStore } from '../../stores';

dayjs.extend(relativeTime);

export default function Notifications() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const {
    notifications,
    isLoading,
    fetchNotifications,
    markAsRead,
    markAllAsRead
  } = useNotificationStore();


  useEffect(() => {
    fetchNotifications();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleNotificationClick = async (notification) => {
    if (!notification.isRead) {
      await markAsRead(notification._id);
    }
    if (notification.link) {
      navigate(notification.link);
    }
  };

  const getNotificationIcon = (type) => {
    switch (type) {
      case 'project_update':
      case 'project_invitation':
        return '📁';
      case 'task_assigned':
      case 'task_update':
        return '📋';
      case 'client_followup':
        return '👤';
      case 'invoice_paid':
        return '💰';
      default:
        return '🔔';
    }
  };

  return (
    <>
      <Helmet>
        <title>{t('notifications.title')} - ProCTM</title>
      </Helmet>

      <PageHeader
        title={t('notifications.title')}
        actions={
          notifications.some(n => !n.isRead) && (
            <Button variant="outline" icon={CheckIcon} onClick={markAllAsRead}>
              {t('notifications.markAllRead')}
            </Button>
          )
        }
      />

      <Card>
        <CardBody className="p-0">
          {isLoading ? (
            <PageLoader />
          ) : notifications.length === 0 ? (
            <EmptyState
              icon={BellIcon}
              title={t('notifications.noNotifications')}
              className="py-12"
            />
          ) : (
            <div className="divide-y divide-gray-100">
              {notifications.map((notification) => (
                <div
                  key={notification._id}
                  onClick={() => handleNotificationClick(notification)}
                  className={`flex items-start gap-4 p-4 cursor-pointer transition-colors ${notification.isRead ? 'bg-white' : 'bg-primary/5 border-l-4 border-primary'
                    } hover:bg-gray-50`}
                >
                  <div className="flex-shrink-0">
                    <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center text-lg">
                      {getNotificationIcon(notification.type)}
                    </div>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2 mb-1">
                      <p className={`font-semibold text-sm ${notification.isRead ? 'text-gray-600' : 'text-neutral-dark'}`}>
                        {notification.title}
                      </p>
                      {notification.metadata?.actionBy && (
                        <Avatar
                          src={notification.metadata.actionBy?.avatar ? (notification.metadata.actionBy.avatar.startsWith('http') ? notification.metadata.actionBy.avatar : process.env.REACT_APP_SOCKET_URL + notification.metadata.actionBy.avatar) : null}
                          name={`${notification.metadata.actionBy?.firstName} ${notification.metadata.actionBy?.lastName}`}
                          size="xs"
                        />
                      )}
                    </div>
                    <p className="text-sm text-gray-600 line-clamp-2 mb-2">{notification.message}</p>
                    <div className="flex items-center gap-2">
                      <p className="text-xs text-gray-400">
                        {dayjs(notification.createdAt).fromNow()}
                      </p>
                      {notification.metadata?.entityType && (
                        <Badge variant="neutral" className="text-xs">
                          {notification.metadata.entityType}
                        </Badge>
                      )}
                    </div>
                  </div>
                  {!notification.isRead && (
                    <div className="w-2 h-2 bg-primary rounded-full mt-2 flex-shrink-0" />
                  )}
                </div>
              ))}
            </div>
          )}
        </CardBody>
      </Card>
    </>
  );
}
